import { CombinedGraphQLErrors, TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Mutation,
  MutationDeleteVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs
} from 'graphql/generated/graphqlTypes';
import { DELETE_VISITOR_BY_ID } from 'graphql/v2/mutations';
import { GET_VISITOR_DETAILS } from 'graphql/v2/queries';
import { useEffect } from 'react';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { refetchQueries } from 'v2/hooks/useAllVisitors/useAllVisitors';
import { TranslatedHookProps } from '../../../shared/types';

interface Props extends TranslatedHookProps {
  visitorId?: string;
}

export const isVisitorNotFoundError = (error?: unknown) =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some((graphQLError) => 'errorType' in graphQLError && graphQLError.errorType === 'UserError');

// Typing the document (instead of passing generics to useQuery) avoids the deprecated useQuery signature.
const GET_VISITOR_DETAILS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorById'>,
  QueryGetVisitorByIdArgs
> = GET_VISITOR_DETAILS;

const DELETE_VISITOR_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteVisitorById'>,
  MutationDeleteVisitorByIdArgs
> = DELETE_VISITOR_BY_ID;

const useVisitorDetails = ({ handleFetchError, translation, visitorId }: Props) => {
  // Apollo 4 notifies on network status changes by default, which flips `loading` to true on every refetch or
  // poll. Keep it off so a refetch does not blank content that is already on screen.
  const {
    loading: visitorDetailsLoading,
    data: visitorDetailsData,
    error: visitorDetailsError,
    refetch: refetchVisitorDetails
  } = useQuery(GET_VISITOR_DETAILS_TYPED, {
    variables: { id: visitorId! },
    skip: visitorId === undefined,
    notifyOnNetworkStatusChange: false
  });

  const isVisitorNotFound = isVisitorNotFoundError(visitorDetailsError);

  useEffect(() => {
    if (visitorDetailsError) {
      // A missing visitor already renders the 404 page, so no error snackbar is needed here.
      if (!isVisitorNotFound) {
        handleFetchError(translation.user_details_error_fetching);
      }
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const [deleteVisitorMutation, { loading: deleteVisitorLoading }] = useMutation(DELETE_VISITOR_BY_ID_TYPED, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      handleFetchError(translation.external_groups_user_error_deleting);
      console.error(error);
    }
  });

  const deleteVisitor = async (visitorId: string) => {
    const { data } = await ignoreHandledMutationError(
      deleteVisitorMutation({
        variables: { visitorId }
      })
    );
    return data;
  };

  return {
    visitorDetails: visitorDetailsData?.getVisitorById,
    loadingVisitorDetails: visitorDetailsLoading,
    isVisitorNotFound,
    hasVisitorFetchError: Boolean(visitorDetailsError) && !isVisitorNotFound,
    // A failed retry is already reported through `visitorDetailsError`, so the rejection is only swallowed here.
    retryFetchingVisitorDetails: () => refetchVisitorDetails().catch(() => undefined),
    deleteVisitor,
    deleteVisitorLoading
  };
};

export default useVisitorDetails;
